import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { signedEditionBlockchainCertService } from "../services/signedEditionBlockchainCertService";
import { signedEditionBlockchainCertDisplayId } from "./SignedEditionBlockchainCertCommonStore";
import { SignedEditionBlockchainCert } from "../../../api/graphql";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { InitializeParams } from "../../../store/types";

export class SignedEditionBlockchainCertViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = signedEditionBlockchainCertService;
  signedEditionBlockchainCertInstance: SignedEditionBlockchainCert | null =
    null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  contractNumberValue: string = "";
  fileFiles: CrudRows = {};
  confirmedAtValue: Date | null = null;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSignedEditionBlockchainCertById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.signedEditionBlockchainCertOne({ id });
    this.signedEditionBlockchainCertInstance =
      result.signedEditionBlockchainCertOne;
    if (this.signedEditionBlockchainCertInstance) {
      this.loadValues(this.signedEditionBlockchainCertInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.signedEditionBlockchainCertInstance) {
      return signedEditionBlockchainCertDisplayId(
        this.signedEditionBlockchainCertInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SignedEditionBlockchainCertSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "SignedEditionBlockchainCert",
      fieldName: fieldName,
      instanceId: this.signedEditionBlockchainCertInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "SignedEditionBlockchainCert",
        fieldName: fieldName,
        instanceId: this.signedEditionBlockchainCertInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.signedEditionBlockchainCertInstance = null;
    this.nameValue = "";
    this.contractNumberValue = "";
    this.fileFiles = {};
    this.confirmedAtValue = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSignedEditionBlockchainCert() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editSignedEditionBlockchainCert() {
    const id = this.signedEditionBlockchainCertInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/signedEditionBlockchainCert/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSignedEditionBlockchainCert(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSignedEditionBlockchainCert(): any {
    yield this.close();
  }

  loadValues(instance: SignedEditionBlockchainCert) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.contractNumberValue = instance.contractNumber;
    this.fileFiles = instance.file_data
      ? {
          [instance.file_data.name]: {
            name: instance.file_data.name,
            formattedSize: formatBytes(instance.file_data?.size || 0),
            mimetype: instance.file_data?.mimetype,
            id: instance.file_data.name,
            __status__: "original",
          },
        }
      : {};
    this.confirmedAtValue = new Date(instance.confirmedAt);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSignedEditionBlockchainCertById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
